// src/pages/driver/driverJobList/RunDocketSection.jsx — per-run weighbridge docket upload +
// comment editor, backed by the `weighbridgeDockets` collection.
import { useEffect, useState } from "react";
import { db } from "../../../services/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import PhotoUploadBase64 from "../../../components/PhotoUploadBase64";

function RunDocketComment({ initial, onSave }) {
  const [value, setValue] = useState(initial || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(initial || "");
    setDirty(false);
  }, [initial]);

  return (
    <div className="mt-3">
      <label className="block text-xs font-medium text-gray-700 mb-1">
        Driver comment (weighbridge docket)
      </label>
      <textarea
        rows={3}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400"
        placeholder="Add any notes about this weighbridge docket"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setDirty(true);
        }}
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={async () => {
            if (!dirty || saving) return;
            setSaving(true);
            try {
              await onSave(value);
              setDirty(false);
            } finally {
              setSaving(false);
            }
          }}
          disabled={!dirty || saving}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition"
        >
          {saving ? "Saving..." : "Save comment"}
        </button>
        {!dirty && !saving && (
          <span className="text-xs text-gray-500">Saved</span>
        )}
      </div>
    </div>
  );
}

export default function RunDocketSection({
  driverId,
  date,
  companyId,
  runKey,
  docketIndex,
  existingPhotos,
  refresh,
  assignmentDocId,
  runDocMap,
}) {
  const [initialComment, setInitialComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const photoCount = Array.isArray(existingPhotos) ? existingPhotos.length : 0;

  const docId = `${companyId || "default"}_${driverId}_${date}_${runKey}`;

  useEffect(() => {
    let active = true;

    setLoading(true);
    setInitialComment("");

    (async () => {
      try {
        const ref = doc(db, "weighbridgeDockets", docId);
        const snap = await getDoc(ref);
        if (!active) return;
        if (snap.exists()) {
          const d = snap.data() || {};
          setInitialComment(d.comment || "");
        }
      } catch (e) {
        console.error("Failed to load weighbridge docket comment:", e);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [docId]);

  async function saveComment(text) {
    const ref = doc(db, "weighbridgeDockets", docId);
    await setDoc(
      ref,
      {
        driverId,
        date,
        companyId: companyId || "",
        runKey,
        comment: text || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 p-3 text-left sm:p-4"
      >
        <span className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 text-sm sm:text-base">
            Weighbridge docket – {runKey}
          </span>
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
              photoCount > 0
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "border-gray-300 bg-white text-gray-600"
            }`}
          >
            {photoCount > 0 ? "Recorded" : "Not recorded"}
          </span>
        </span>
        <svg
          className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {open && (
        <div className="px-3 pb-3 sm:px-4 sm:pb-4">
          <p className="text-xs text-gray-600">
            Upload the weighbridge docket and add any comments for this run. This is
            protocol only, not a job.
          </p>

          <div className="mt-3">
            <PhotoUploadBase64
              jobId={`${driverId}_${date}`}
              assignmentDocId={
                runDocMap?.[runKey] || assignmentDocId || `${driverId}_${date}`
              }
              runKey={runKey}
              index={docketIndex}
              existingPhotos={existingPhotos}
              onUpdate={refresh}
            />
          </div>

          {loading ? (
            <p className="mt-2 text-xs text-gray-500">Loading docket comment...</p>
          ) : (
            <RunDocketComment
              key={docId}
              initial={initialComment}
              onSave={saveComment}
            />
          )}
        </div>
      )}
    </div>
  );
}
